import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import useLogin from "../hooks/useLogin";

// Coloca tus archivos en /src/assets/
import logoLight from "../assets/logo.png"; // para modo claro (logo oscuro)
import logoDark from "../assets/logo.png"; // para modo oscuro (logo claro)
import MARCA from "../config/marca";

const LoginPage = () => {
  const { username, password, rememberMe, error, loading, handleChange, handleLogin } =
    useLogin();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);

  const dark = theme.palette.mode === "dark";
  const logoSrc = MARCA.logo ? (dark ? MARCA.logoBlanco : MARCA.logo) : (dark ? logoDark : logoLight);
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 4,
        position: "relative",
        overflow: "hidden",
        backgroundColor: theme.palette.background.default,
        // Halos suaves con los colores de marca
        "&::before, &::after": {
          content: '""',
          position: "absolute",
          borderRadius: "50%",
          filter: "blur(90px)",
          pointerEvents: "none",
        },
        "&::before": {
          width: 480,
          height: 480,
          top: -140,
          left: -140,
          background: alpha(primary, dark ? 0.28 : 0.18),
        },
        "&::after": {
          width: 420,
          height: 420,
          bottom: -120,
          right: -120,
          background: alpha(secondary, dark ? 0.22 : 0.14),
        },
      }}
    >
      <Paper
        elevation={dark ? 8 : 4}
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 420,
          p: { xs: 3, sm: 5 },
          borderRadius: "18px",
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden", // recorta la barra de acento con la curva de la tarjeta
          // Línea de acento superior con el gradiente de marca
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${primary}, ${secondary})`,
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
          <Box
            component="img"
            src={logoSrc}
            alt="Logo"
            sx={{ width: 150, height: 68, objectFit: "contain", mb: 2 }}
          />
          <Typography component="h1" variant="h5" sx={{ fontWeight: 800 }}>
            Bienvenido
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Inicia sesión para continuar
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleLogin} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Usuario"
            name="username"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineRoundedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Contraseña"
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                name="rememberMe"
                checked={rememberMe}
                onChange={handleChange}
                size="small"
              />
            }
            label={<Typography variant="body2">Recordar por 30 días</Typography>}
            sx={{ mt: 0.5 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            loading={loading}
            sx={{
              mt: 3,
              py: 1.2,
              fontWeight: 700,
              boxShadow: `0 8px 20px ${alpha(primary, 0.35)}`,
              "&:hover": { boxShadow: `0 6px 16px ${alpha(primary, 0.45)}` },
            }}
          >
            Ingresar
          </Button>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", mt: 3 }}
        >
          © {new Date().getFullYear()} SORT · Todos los derechos reservados
        </Typography>
      </Paper>
    </Box>
  );
};

export default LoginPage;
